import React, { useState } from 'react';
import { Button, CircularProgress, Typography, styled, SxProps, Theme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// --- Types ---
type ButtonColor = 'default' | 'success' | 'error' | 'warning';
type ButtonSize = 'small' | 'medium' | 'large';

interface CheckoutButtonProps {
  onCheckout: () => void | Promise<void>;
  color?: ButtonColor;
  size?: ButtonSize;
  style?: React.CSSProperties;
  label?: string;
  sx?: SxProps<Theme>;
}

// --- Styled Components ---

const Shimmer = styled(motion.div)({
  position: 'absolute',
  top: 0,
  left: '-100%',
  width: '50%',
  height: '100%',
  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
  transform: 'skewX(-25deg)',
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'customColor' && prop !== 'customSize',
})<{ customColor: ButtonColor; customSize: ButtonSize }>(({ theme, customColor, customSize }) => {
  // Color Mapping
  const gradients = {
    default: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
    success: 'linear-gradient(45deg, #43A047 30%, #66BB6A 90%)',
    error: 'linear-gradient(45deg, #E53935 30%, #FF5252 90%)',
    warning: 'linear-gradient(45deg, #F4511E 30%, #FF8A65 90%)',
  };

  const shadowColors = {
    default: 'rgba(33, 150, 243, 0.4)',
    success: 'rgba(67, 160, 71, 0.4)',
    error: 'rgba(229, 57, 53, 0.4)',
    warning: 'rgba(244, 81, 30, 0.4)',
  };

  // Size Mapping
  const sizes = {
    small: { padding: '8px 16px', fontSize: '0.9rem', icon: 18 },
    medium: { padding: '12px 24px', fontSize: '1.1rem', icon: 24 },
    large: { padding: '18px 36px', fontSize: '1.4rem', icon: 32 },
  };

  return {
    background: gradients[customColor],
    padding: sizes[customSize].padding,
    fontSize: sizes[customSize].fontSize,
    borderRadius: '12px',
    fontWeight: 700,
    color: '#fff',
    textTransform: 'none',
    boxShadow: `0 4px 14px 0 ${shadowColors[customColor]}`,
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      background: gradients[customColor],
      filter: 'brightness(1.1)',
      boxShadow: `0 6px 20px 0 ${shadowColors[customColor]}`,
    },
    '&.Mui-disabled': {
      background: '#e0e0e0',
      color: '#9e9e9e',
    },
  };
});

// --- Main Component ---

export const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  onCheckout,
  color = 'default',
  size = 'medium',
  style,
  label = 'Checkout',
  sx,
}) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleClick = async () => {
    setStatus('loading');
    try {
      await onCheckout();
      setStatus('success');
      setTimeout(() => setStatus('idle'), 3000); // Reset after 3 seconds
    } catch (error) {
      setStatus('idle');
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      style={{ width: '100%', ...style }}
    >
      <StyledButton
        fullWidth
        customColor={color}
        customSize={size}
        onClick={handleClick}
        disabled={status === 'loading'}
        sx={sx}
      >
        {/* Animated Shimmer Effect */}
        {status === 'idle' && (
          <Shimmer
            animate={{ left: ['-100%', '200%'] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}
          />
        )}

        <AnimatePresence mode="wait">
          {status === 'loading' ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <CircularProgress size={size === 'small' ? 20 : 28} sx={{ color: 'white' }} />
            </motion.div>
          ) : status === 'success' ? (
            <motion.div
              key="success"
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <CheckCircleIcon fontSize={size === 'small' ? 'small' : 'medium'} />
              <span>Success</span>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
            >
              <ShoppingCartCheckoutIcon fontSize={size === 'small' ? 'small' : 'medium'} />
              <Typography variant="inherit">{label}</Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledButton>
    </motion.div>
  );
};